import * as Sentry from "@sentry/browser";
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Container, useToast } from "../../components";
import {
  useDisplaySavingAmountFeatureFlag,
  useMeetingRoomDailyPricingFeatureFlag,
  useMrGalleryImprovementsFeatureFlag,
  useMrMultiDayBookingFeatureFlag,
  useMrNonConsecutiveDateRangeFeatureFlag,
  useOfferingDailyAccessQuantityFieldFeatureFlag,
} from "../../core/splitio";
import { PricingType, Space, OrderableType, Location } from "../../data";
import {
  findIsSoldOut,
  hasFoundOfferingFromQueryLocationId,
  MembershipDetail,
  PartnerOfferingLocation,
  shouldHideLocationAndCityPicker,
} from "../../data/partner_offering";
import { formatCurrency } from "../../lib/currency/currency";
import { FirstDayOfWeek, parseDay } from "../../lib/datetime";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { getSystemLocale } from "../../lib/locale/locale";
import {
  AvailableDay,
  AvailableTimeSlot,
  BookingType,
  PromotionLine,
  Quote,
  SpaceOffering,
  useGetAvailableDaysMembership,
  useSpaceBookingPolicyQuery,
} from "../../offering/api";
import { QuantityNotAvailableErrorMessage } from "../../offering/checkout_box_mobile";
import {
  DailyAccessOrderParams,
  DailyAccessOrderParamsErrors,
  DailyOfficeOrderParams,
  DailyOfficeOrderParamsErrors,
  DayPassDateRange,
  MeetingRoomOrderParams,
  MeetingRoomOrderParamsErrors,
  MembershipOrderParams,
  MembershipOrderParamsErrors,
} from "../../offering/order_params";
import { Spacer } from "../../offering/spacer";
import { CheckoutDatePicker } from "./checkout_date_picker";
import { GuestPicker } from "./guest_picker";
import { PartnerLocationPicker } from "./partner_location_picker";
import { QuantityPickerV2 } from "./quantity_picker_v2";
import { Text } from "../../offering/text";
import { CapacityPickerV2 } from "./capacity_picker_v2";
import { MembershipLocationPickerWrapper, SpacePicker } from "./space_picker";
import { Button, ListGroup, Modal } from "react-bootstrap";
import { useMediaQuery, useQueryString } from "../../hooks";
import { CityPicker, MembershipCityPicker } from "./city_picker";
import { uniqueBy } from "../../lib/js_utils/array_utils";
import { useAnalytics } from "../../lib/analytics/analytics";
import { AttendeesPicker } from "./attendees_picker";
import { getFirstDayOfNextMonthAfterNMonths } from "../../offering/date_utils";
import { Step, Steps } from "./steps_membership";
import { isPartnerLocation } from "../../data/api";
import { useParams } from "react-router-dom";
import { CreditOption, CreditQuantityPicker } from "./credit_quantity_picker";
import { addMonths, isAfter, isBefore, toDay } from "../../offering/day_utils";
import { PriceLabel, PriceLabelDesktop } from "./price_label";
import classNames from "classnames";
import { calculateDailyBenefitPercent } from "../utils/calculate_daily_benefit_percent";
import { getMaxHoursFromBookingHours } from "../utils/get_max_hours_from_booking_hours";
import { getMembershipCheckoutBoxTimelineSteps } from "../utils/get_membership_checkout_box_timeline_steps";
import { getPromoMonthsLeftAfterFirstPayment } from "../utils/get_promo_months_left_after_first_payment";
import { shouldDisplayTimelineInMembershipCheckoutBox } from "../utils/should_display_timeline_in_membership_checkout_box";
import { CheckoutDateTimePicker } from "./date-time-picker/checkout_datetime_picker";
import { DateTimeValue } from "./date-time-picker/date_time_picker";
import { PickingRange } from "./date_range_picker/date_range_picker";
import { MembershipDatePickerWrapper } from "./membership_date_picker_wrapper";
import { calculateSavingAmount } from "../utils/calculate_saving_amount";
import { SavingAmountLabel } from "./saving_amount_label";
import { QuoteDetails, TotalPrice } from "./quote_view";
import { Divider } from "@/offering/divider";
import { useIsWework } from "@/offering/hooks/use_is_wework";
import { DiscountPopupContext } from "@/core/discount_popup_provider";
import {
  getStartDateFromOrderParams,
  getEndDateFromOrderParams,
} from "@/offering/order_params_utils";

interface MeetingRoomCheckoutBoxDesktopV2Props {
  quote?: Quote | null | undefined;
  space: Space | null | undefined;
  orderParams: MeetingRoomOrderParams;
  orderParamsErrors?: MeetingRoomOrderParamsErrors;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeDateRange: (dateRange?: DateTimeValue) => void;
  onContinue?: () => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  onSelectDate: (date: Date) => void;
  onChangeAttendees: (value: string) => void;
  timeslots: AvailableTimeSlot[];
  firstDayOfWeek?: FirstDayOfWeek;
  availableDaysError?: string;
  availableTimeSlotsError?: string;
  datePickerPlaceholder?: string;
  showCTA?: boolean;
  showPriceInfoMessage?: boolean;
  availableDays?: AvailableDay[];
  disableRangeSelection?: boolean;
  showQuoteDetails?: boolean;
}

export function MeetingRoomCheckoutBoxDesktopV2(
  props: MeetingRoomCheckoutBoxDesktopV2Props
) {
  const {
    quote,
    orderParams,
    orderParamsErrors,
    space,
    timeslots,
    firstDayOfWeek,
    onChangeDateRange,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    onNextVisibleDates,
    onSelectDate,
    onChangeAttendees,
    availableDaysError,
    availableTimeSlotsError,
    datePickerPlaceholder,
    showCTA,
    showPriceInfoMessage,
    availableDays,
    disableRangeSelection,
    showQuoteDetails,
  } = props;
  const { fbt } = useI18n();
  const { device } = useMediaQuery();
  const mrMultiDayBookingFeatureFlag = useMrMultiDayBookingFeatureFlag(
    space?.partnerID || ""
  );
  const mrNonConsecutiveDateRangeFeatureFlag =
    useMrNonConsecutiveDateRangeFeatureFlag(space?.partnerID || "");

  let pickingRange = PickingRange.OneDate;
  if (!disableRangeSelection) {
    if (mrMultiDayBookingFeatureFlag) {
      pickingRange = PickingRange.Consecutive;
    }
    if (mrNonConsecutiveDateRangeFeatureFlag) {
      pickingRange = PickingRange.NonConsecutive;
    }
  }

  const [hasAvailabilityError, setHasAvailabilityError] = useState(false);
  const [hasShownAvailabilityError, setHasShownAvailabilityError] =
    useState(false);
  const toast = useToast();

  useEffect(() => {
    const hasError = !!availableDaysError || !!availableTimeSlotsError;
    setHasAvailabilityError(hasError);
    if (!hasError) {
      setHasShownAvailabilityError(false);
    } else if (!hasShownAvailabilityError) {
      toast.error({ message: fbt("availabilityFetchingError") });
      Sentry.withScope((scope) => {
        scope.setExtras({ space_id: space?.id });
        Sentry.captureMessage(
          `Unable to check availability ${
            availableDaysError || availableTimeSlotsError
          }`
        );
      });
      setHasShownAvailabilityError(true);
    }
  }, [
    availableDaysError,
    availableTimeSlotsError,
    hasShownAvailabilityError,
    toast,
    fbt,
    space,
  ]);

  const spaceLayout = useMemo(() => space?.layouts[0], [space]);
  const capacity = spaceLayout?.capacity;

  const numberOfGuestOptions = useMemo(() => {
    if (!capacity) {
      return [];
    }

    return Array.from(Array(capacity).keys()).map((value) => ({
      value: value + 1,
      label: (value + 1).toString(),
    }));
  }, [capacity]);

  const { data: spaceBookingPolicy } = useSpaceBookingPolicyQuery(
    space?.id,
    getStartDateFromOrderParams(orderParams),
    getEndDateFromOrderParams(orderParams)
  );

  const askForNumberOfGuests = useMemo(() => {
    if (!spaceLayout || !spaceBookingPolicy) {
      return false;
    }

    return (
      spaceLayout.capacity > 1 && !!spaceBookingPolicy.askForNumberOfGuests
    );
  }, [spaceLayout, spaceBookingPolicy]);

  const otherPickers = [
    askForNumberOfGuests && capacity ? (
      <AttendeesPicker
        options={numberOfGuestOptions}
        onChange={onChangeAttendees}
        value={orderParams.attendees}
      />
    ) : null,
  ];
  const numberOfOtherPickers = otherPickers.filter((p) => !!p).length;

  return (
    <CheckoutBoxDesktopV2
      quote={quote}
      error={orderParamsErrors?.dateRange || orderParamsErrors?.spaceID}
      space={space}
      onContinue={onContinue}
      showCTA={showCTA}
      priceType={PricingType.Hourly}
      showPriceInfoMessage={showPriceInfoMessage}
      showQuoteDetails={showQuoteDetails}
      pickers={[
        <CheckoutDateTimePicker
          firstDayOfWeek={firstDayOfWeek}
          value={orderParams.dateRange}
          isBlockedDate={isBlockedDate}
          isOutsideRange={isOutsideRange}
          onNextVisibleDates={onNextVisibleDates}
          onComplete={(v) => onChangeDateRange(v)}
          onSelectDate={onSelectDate}
          disabled={hasAvailabilityError}
          placeholder={datePickerPlaceholder}
          timeslots={timeslots}
          availableDays={availableDays}
          space={space}
          pickingRange={pickingRange}
          onSave={
            !numberOfOtherPickers && device === "mobile"
              ? onContinue
              : undefined
          }
        />,
        ...otherPickers,
      ]}
    />
  );
}

interface DailyAccessCheckoutBoxDesktopV2Props {
  quote: Quote | undefined;
  quoteError?: string;
  quoteLoading: boolean;
  includeQuantity: boolean;
  locations: PartnerOfferingLocation[];
  space: Space | null | undefined;
  location: Location | null | undefined;
  orderParams: DailyAccessOrderParams;
  orderParamsErrors?: DailyAccessOrderParamsErrors;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeLocation: (spaceID: string) => void;
  onChangeCity: (city: string) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  onChangeQuantity: (qty: number) => void;
  multiPasses: SpaceOffering[];
  credits: CreditOption[];
  onChangeAddOnID: (addOnID: string, spaceOfferingID: string) => void;
  onContinue?: () => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  availableDays?: Date[];
  multiDayBookingAllowed: boolean;

  // UI overrides
  showCTA?: boolean;
  datePickerPlaceholder?: string;
  showCheckoutPrice?: boolean;
  showDiscountLabel?: boolean;
  showPriceInfoMessage?: boolean;
  showQuoteDetails?: boolean;
  singleDaySelect?: boolean;
}

export function DailyAccessCheckoutBoxDesktopV2(
  props: DailyAccessCheckoutBoxDesktopV2Props
) {
  const {
    includeQuantity,
    quote,
    quoteError,
    multiPasses,
    orderParams,
    orderParamsErrors,
    locations,
    space,
    location,
    onChangeLocation,
    onChangeAddOnID,
    onChangeDateRange,
    onChangeQuantity,
    onChangeCity,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    onNextVisibleDates,
    firstDayOfWeek,
    showCTA,
    showCheckoutPrice,
    showDiscountLabel,
    datePickerPlaceholder,
    showPriceInfoMessage,
    availableDays,
    multiDayBookingAllowed,
    credits,
    showQuoteDetails,
    singleDaySelect,
  } = props;
  let error;

  if (orderParamsErrors?.dateRange) {
    error = <Text color="error">{orderParamsErrors?.dateRange}</Text>;
  } else if (orderParamsErrors?.spaceID) {
    error = <Text color="error">{orderParamsErrors?.spaceID}</Text>;
  } else if (orderParamsErrors?.addOnID) {
    error = <Text color="error">{orderParamsErrors?.addOnID}</Text>;
  } else if (includeQuantity && quoteError) {
    error = <QuantityNotAvailableErrorMessage orderParams={orderParams} />;
  }

  const mp = multiPasses.find(
    (mp) =>
      mp.addOnID === orderParams.addOnID || orderParams.addOnID === "daypass"
  );
  const displayQuantityField =
    useOfferingDailyAccessQuantityFieldFeatureFlag() &&
    !!space?.productType?.selectQuantity;
  const currencyCode = space?.currencyCode;
  const filteredLocations = locations.filter((l) =>
    orderParams.city ? l.location.city === orderParams.city : l
  );

  const handleChangeCity = useCallback(
    (city) => {
      onChangeCity(city);
      const cityLocations = locations.filter(
        (location) => location.location.city === city
      );
      if (cityLocations.length === 1) {
        onChangeLocation(cityLocations[0].spaceID);
      }
    },
    [locations, onChangeCity, onChangeLocation]
  );

  const cities = useMemo(
    () =>
      uniqueBy(
        locations.map((l) => ({
          value: l.location.city as string,
          label: l.location.city as string,
          data: l.location,
        })),
        (l) => l.value
      ),
    [locations]
  );

  return (
    <CheckoutBoxDesktopV2
      quote={quote}
      error={error}
      space={space}
      onContinue={onContinue}
      showCTA={showCTA}
      priceType={PricingType.Daily}
      showCheckoutPrice={showCheckoutPrice}
      showPriceInfoMessage={showPriceInfoMessage}
      showPrice={!!location}
      showQuoteDetails={showQuoteDetails}
      pickers={[
        cities.length > 1 && (
          <CityPicker
            cities={cities}
            value={orderParams.city}
            onChange={handleChangeCity}
          />
        ),
        <SpacePicker
          value={orderParams.spaceID}
          locations={filteredLocations}
          onChange={onChangeLocation}
          disabled={cities.length > 1 ? !orderParams.city : false}
        />,
        multiPasses.length && (
          <QuantityPickerV2
            multiPasses={multiPasses}
            value={orderParams.addOnID}
            onChange={onChangeAddOnID}
            currency={currencyCode}
            disabled={!orderParams.spaceID}
            showDiscountLabel={showDiscountLabel}
          />
        ),
        credits.length && (
          <CreditQuantityPicker
            options={credits}
            value={orderParams.addOnID}
            onChange={onChangeAddOnID}
            currency={currencyCode}
          />
        ),
        mp?.numberOfEntries &&
          mp.numberOfEntries === 1 &&
          includeQuantity &&
          onChangeQuantity &&
          displayQuantityField && (
            <GuestPicker
              value={orderParams.quantity}
              onChange={onChangeQuantity}
              limitGuestPicker={space?.inventoryCapacity}
              disabled={!orderParams.addOnID}
            />
          ),
        mp?.numberOfEntries && mp.numberOfEntries === 1 && (
          <CheckoutDatePicker
            firstDayOfWeek={firstDayOfWeek}
            value={orderParams.dateRange}
            isBlockedDate={isBlockedDate}
            isOutsideRange={isOutsideRange}
            onNextVisibleDates={onNextVisibleDates}
            onComplete={(v) => onChangeDateRange(v)}
            placeholder={datePickerPlaceholder}
            availableDays={availableDays}
            disableRangeSelection={!multiDayBookingAllowed || !!singleDaySelect}
            space={space}
          />
        ),
      ]}
    />
  );
}

interface DailyOfficeCheckoutBoxDesktopProps {
  quote: Quote | undefined;
  quoteError?: string;
  capacities: number[];
  multiPasses: SpaceOffering[];
  capacityOptionLabel?: string;
  quoteLoading?: boolean;
  locations: PartnerOfferingLocation[];
  space: Space | null | undefined;
  location: Location | null | undefined;
  orderParams: DailyOfficeOrderParams;
  orderParamsErrors?: DailyOfficeOrderParamsErrors;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeLocation: (spaceID: string) => void;
  onChangeCity: (city: string) => void;
  onChangeCapacity: (qty: number) => void;
  onChangeAddOnID: (addOnID: string, spaceOfferingID: string) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  onContinue?: () => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  availableDays?: Date[];
  multiDayBookingAllowed: boolean;

  // UI overrides
  showCTA?: boolean;
  datePickerPlaceholder?: string;
  showDiscountLabel?: boolean;
  showPriceInfoMessage?: boolean;
  showQuoteDetails?: boolean;
}

export function DailyOfficeCheckoutBoxDesktop(
  props: DailyOfficeCheckoutBoxDesktopProps
) {
  const {
    quote,
    capacities,
    multiPasses,
    capacityOptionLabel,
    orderParams,
    orderParamsErrors,
    locations,
    space,
    location,
    onChangeCity,
    onChangeLocation,
    onChangeCapacity,
    onChangeAddOnID,
    onChangeDateRange,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    onNextVisibleDates,
    firstDayOfWeek,
    showCTA,
    datePickerPlaceholder,
    showDiscountLabel,
    showPriceInfoMessage,
    availableDays,
    multiDayBookingAllowed,
    showQuoteDetails,
  } = props;
  let error;
  const { fbt } = useI18n();

  if (orderParamsErrors?.dateRange) {
    error = <Text color="error">{orderParamsErrors?.dateRange}</Text>;
  } else if (orderParamsErrors?.spaceID) {
    error = <Text color="error">{orderParamsErrors?.spaceID}</Text>;
  } else if (orderParamsErrors?.capacity) {
    error = <Text color="error">{orderParamsErrors?.capacity}</Text>;
  } else if (
    space?.inventoryCapacity !== undefined &&
    space?.inventoryCapacity < 1
  ) {
    error = (
      <Text color="error">
        {fbt("There are no offices available for the selected capacity.")}
      </Text>
    );
  }

  const mp = multiPasses.find(
    (mp) =>
      mp.addOnID === orderParams.addOnID || orderParams.addOnID === "dayoffice"
  );
  const currencyCode = space?.currencyCode;
  const filteredLocations = locations.filter((l) =>
    orderParams.city ? l.location.city === orderParams.city : l
  );

  const handleChangeCity = useCallback(
    (city) => {
      onChangeCity(city);
      const cityLocations = locations.filter(
        (location) => location.location.city === city
      );
      if (cityLocations.length === 1) {
        onChangeLocation(cityLocations[0].id);
      }
    },
    [locations, onChangeCity, onChangeLocation]
  );

  const cities = useMemo(
    () =>
      uniqueBy(
        locations.map((l) => ({
          value: l.location.city as string,
          label: l.location.city as string,
          data: l.location,
        })),
        (l) => l.value
      ),
    [locations]
  );

  return (
    <CheckoutBoxDesktopV2
      quote={quote}
      error={error}
      space={space}
      onContinue={onContinue}
      showCTA={showCTA}
      priceType={PricingType.Daily}
      showPriceInfoMessage={showPriceInfoMessage}
      showPrice={!!location}
      showQuoteDetails={showQuoteDetails}
      pickers={[
        cities.length > 1 && (
          <CityPicker
            cities={cities}
            value={orderParams.city}
            onChange={handleChangeCity}
          />
        ),
        <PartnerLocationPicker
          value={orderParams.partnerOfferingLocationID}
          locations={filteredLocations}
          onChange={onChangeLocation}
          disabled={cities.length > 1 ? !orderParams.city : false}
        />,
        <CapacityPickerV2
          capacities={capacities}
          value={orderParams.capacity}
          onChange={onChangeCapacity}
          optionLabel={capacityOptionLabel}
          disabled={!orderParams.partnerOfferingLocationID}
        />,
        <QuantityPickerV2
          multiPasses={multiPasses}
          value={orderParams.addOnID}
          onChange={onChangeAddOnID}
          currency={currencyCode}
          disabled={!orderParams.capacity}
          showDiscountLabel={showDiscountLabel}
        />,
        mp?.numberOfEntries && mp.numberOfEntries === 1 && (
          <CheckoutDatePicker
            firstDayOfWeek={firstDayOfWeek}
            value={orderParams.dateRange}
            isBlockedDate={isBlockedDate}
            isOutsideRange={isOutsideRange}
            onNextVisibleDates={onNextVisibleDates}
            onComplete={(v) => onChangeDateRange(v)}
            placeholder={datePickerPlaceholder}
            availableDays={availableDays}
            disableRangeSelection={!multiDayBookingAllowed}
            space={space}
          />
        ),
      ]}
    />
  );
}

interface CheckoutBoxDesktopV2Props {
  quote?: Quote | null | undefined;
  space: Space | null | undefined;
  priceType: PricingType;
  error?: React.ReactNode;
  onContinue?: () => void;
  pickers: React.ReactNode[];
  showCTA?: boolean;
  showCheckoutPrice?: boolean;
  showPriceInfoMessage?: boolean;
  showPrice?: boolean;
  showQuoteDetails?: boolean;
}

export function CheckoutBoxDesktopV2(props: CheckoutBoxDesktopV2Props) {
  const {
    quote,
    error,
    space,
    onContinue,
    pickers,
    showCTA = true,
    showCheckoutPrice = true,
    showPriceInfoMessage = false,
    showPrice = true,
    showQuoteDetails,
  } = props;
  const disabled = !quote;
  const activePickers = pickers.filter((p) => !!p);
  const mq = useMediaQuery();
  const { fbt } = useI18n();
  const addOnOrderItem = useMemo(
    () => quote?.orderItems.find((o) => o.type === OrderableType.AddOn),
    [quote]
  );
  const isWeWork = useIsWework();

  const mrGalleryImprovementsFeatureFlag =
    useMrGalleryImprovementsFeatureFlag();

  const meetingRoomDailyPricingFeatureFlag =
    useMeetingRoomDailyPricingFeatureFlag(space?.partnerID || "");
  const displaySavingAmountFlag = useDisplaySavingAmountFeatureFlag();

  const hasDynamicPricingConfigured = Object.values(
    space?.priceRanges || {}
  ).some(
    (priceRange) =>
      !!priceRange.lowestPrice &&
      !!priceRange.highestPrice &&
      priceRange?.lowestPrice !== priceRange?.highestPrice
  );

  const displayPrice = useMemo(() => {
    if (quote) {
      if (
        addOnOrderItem?.type === OrderableType.AddOn &&
        addOnOrderItem.details.numberOfEntries > 0
      ) {
        const price = quote.subTotal / addOnOrderItem.details.numberOfEntries;
        const bookingType = BookingType.Daily;

        return {
          price,
          bookingType,
        };
      } else if (quote.orderItems[0].type === OrderableType.Booking) {
        const price = quote.orderItems[0].unitPrice;
        const bookingType = quote.orderItems[0].details.type;
        return {
          price,
          bookingType,
        };
      }
    } else {
      return undefined;
    }
  }, [quote, addOnOrderItem]);

  let checkoutPriceLabelComponent: JSX.Element | undefined;
  if (space) {
    if (displayPrice) {
      const savingAmount = quote
        ? calculateSavingAmount(quote, space)
        : undefined;
      const savingAmountLabelComponent = displaySavingAmountFlag &&
        !!savingAmount &&
        savingAmount > 0 && (
          <SavingAmountLabel
            price={Math.ceil(savingAmount)}
            currency={space.currencyCode}
          />
        );
      checkoutPriceLabelComponent = (
        <div className="d-flex align-items-center gap-3">
          <UnitPrice
            price={displayPrice.price}
            currency={space.currencyCode}
            priceUnit={fbt(
              displayPrice.bookingType === BookingType.Hourly ? "/hour" : "/day"
            )}
          />
          {savingAmountLabelComponent}
        </div>
      );
    } else {
      let hasDailyPrice = false;
      let hasHourlyPrice = false;
      space.pricings.forEach((p) => {
        if (p.type === PricingType.Daily) {
          hasDailyPrice = true;
        } else if (p.type === PricingType.Hourly) {
          hasHourlyPrice = true;
        }
      });

      if (
        hasDailyPrice &&
        hasHourlyPrice &&
        space.priceRanges.hourly &&
        space.priceRanges.daily &&
        meetingRoomDailyPricingFeatureFlag
      ) {
        const maxHours = getMaxHoursFromBookingHours(space.bookingHours);

        const savingPercent = calculateDailyBenefitPercent(
          space.pricings.find((p) => p.type === PricingType.Hourly)?.price,
          space.priceRanges.daily.lowestPrice,
          maxHours?.openAt,
          maxHours?.closedAt
        );
        checkoutPriceLabelComponent = (
          <PriceLabelDesktop
            priceRanges={space.priceRanges}
            currency={space.currencyCode}
            hourlyPrice={space.priceRanges.hourly.lowestPrice}
            dailySavingPercent={savingPercent}
          />
        );
      } else if (hasHourlyPrice && space.priceRanges.hourly) {
        checkoutPriceLabelComponent = (
          <UnitPriceRange
            lowestPrice={space.priceRanges.hourly.lowestPrice}
            highestPrice={space.priceRanges.hourly.highestPrice}
            currency={space.currencyCode}
            priceUnit={isWeWork ? fbt("before VAT per hour") : fbt("/hour")}
          />
        );
      } else if (hasDailyPrice && space.priceRanges.daily) {
        checkoutPriceLabelComponent = (
          <UnitPriceRange
            lowestPrice={space.priceRanges.daily.lowestPrice}
            highestPrice={space.priceRanges.daily.highestPrice}
            currency={space.currencyCode}
            priceUnit={isWeWork ? ` ${fbt("before VAT per day")}` : fbt("/day")}
          />
        );
      }
    }
  }

  return (
    <div
      data-testid="checkout-box-desktop"
      className={`${
        mq.device === "mobile"
          ? showCTA
            ? "checkout-box-mobile-v2"
            : ""
          : showCTA
          ? "checkout-box-desktop-v2"
          : ""
      }`}
    >
      {space && showCTA && showCheckoutPrice && (
        <div>
          {showPrice ? (
            <div>{checkoutPriceLabelComponent}</div>
          ) : (
            <h3>{fbt("Select a location")}</h3>
          )}
          <Spacer size={24} />
        </div>
      )}
      <CheckoutBoxDetails pickers={activePickers} />
      {error && (
        <Container paddingTop={16} paddingHorizontal={16}>
          {error}
        </Container>
      )}
      {mq.device === "mobile"
        ? showCTA && (
            <div
              className={
                !mrGalleryImprovementsFeatureFlag ? "col-4" : undefined
              }
            >
              <CheckoutCTA onContinue={onContinue} disabled={disabled} />
            </div>
          )
        : showCTA && (
            <CheckoutCTA onContinue={onContinue} disabled={disabled} />
          )}
      {showPriceInfoMessage &&
        space?.priceRanges &&
        !mrGalleryImprovementsFeatureFlag && (
          <>
            <Spacer size={24} />
            <span> {fbt("priceInfoMessage")} </span>
          </>
        )}
      {showPriceInfoMessage && mrGalleryImprovementsFeatureFlag && (
        <>
          <Spacer size={24} />
          <span className="text-size-xs">
            {hasDynamicPricingConfigured ? `${fbt("priceInfoMessageV2")} ` : ""}
            {fbt("priceChargedMessage")}
          </span>
        </>
      )}

      {mrGalleryImprovementsFeatureFlag && quote && showQuoteDetails && (
        <div className="product-detail-quote-details">
          <Spacer size={24} />
          <Divider />
          <Spacer size={16} />
          <QuoteDetails quote={quote} currency={space?.currencyCode || null} />
          <Spacer size={24} />
          <Divider />
          <Spacer size={24} />
          <TotalPrice
            total={quote.totalPrice}
            currency={space?.currencyCode || null}
          />
        </div>
      )}
    </div>
  );
}

interface CheckoutBoxForMembershipDesktopV2Props {
  quote?: Quote | null | undefined;
  location: Location | null | undefined;
  priceType: PricingType;
  error?: React.ReactNode;
  onContinue?: () => void;
  pickers: React.ReactNode[];
  showCTA?: boolean;
  showCheckoutPrice?: boolean;
  showPriceInfoMessage?: boolean;
  showPrice?: boolean;
  selectedMembership?: MembershipDetail;
  hideLocationAndCityPicker: boolean;
  quoteError?: string;
  membershipSoldOut: boolean;
  allSoldOut: boolean;
}

export function CheckoutBoxForMembershipDesktop(
  props: CheckoutBoxForMembershipDesktopV2Props
) {
  const {
    quote,
    error,
    location,
    onContinue,
    pickers,
    showCTA = true,
    showCheckoutPrice = true,
    showPriceInfoMessage = false,
    showPrice = true,
    selectedMembership,
    hideLocationAndCityPicker,
    quoteError,
  } = props;
  const disabled = !quote && !hideLocationAndCityPicker;
  const activePickers = pickers.filter((p) => !!p);
  const mq = useMediaQuery();
  const { fbt } = useI18n();
  return (
    <div
      className={`${
        mq.device === "mobile"
          ? showCTA
            ? "checkout-box-mobile-v2"
            : ""
          : showCTA
          ? "checkout-box-desktop-v2"
          : ""
      }`}
    >
      {((!isPartnerLocation(selectedMembership?.locatable) && !location) ||
        !!location) &&
        showCTA &&
        showCheckoutPrice && (
          <div>
            {(quote || (selectedMembership && hideLocationAndCityPicker)) &&
              showCTA &&
              showCheckoutPrice &&
              showPrice && (
                <Fragment>
                  <MembershipUnitPrice
                    price={
                      selectedMembership
                        ? selectedMembership.monthlyPrice
                        : quote?.subTotal || 0
                    }
                    priceUnit={fbt("/month")}
                    currency={
                      selectedMembership
                        ? selectedMembership.currency
                        : quote?.currency || ""
                    }
                  />
                </Fragment>
              )}
            <Spacer size={24} />
          </div>
        )}
      <CheckoutBoxDetails pickers={activePickers} />
      {error && (
        <Container paddingTop={16} paddingHorizontal={16}>
          {error}
        </Container>
      )}

      {mq.device === "mobile"
        ? showCTA && (
            <div className="col-4">
              <CheckoutCTA
                onContinue={onContinue}
                disabled={disabled || !!quoteError}
              />
            </div>
          )
        : showCTA && (
            <CheckoutCTA
              onContinue={onContinue}
              disabled={disabled || !!quoteError}
            />
          )}

      {showPriceInfoMessage && (
        <>
          <Spacer size={24} />
          <span> {fbt("priceInfoMessage")} </span>
        </>
      )}
    </div>
  );
}

export function CheckoutBoxForMembershipDesktopV2(
  props: CheckoutBoxForMembershipDesktopV2Props
) {
  const {
    quote,
    error,
    location,
    onContinue,
    pickers,
    showCTA = true,
    showCheckoutPrice = true,
    showPrice = true,
    selectedMembership,
    hideLocationAndCityPicker,
    membershipSoldOut,
    allSoldOut,
  } = props;
  const disabled = hideLocationAndCityPicker ? !quote : !quote || !location;
  const activePickers = pickers.filter((p) => !!p);
  const mq = useMediaQuery();
  const { fbt } = useI18n();

  return (
    <div
      className={`${
        mq.device === "mobile"
          ? showCTA
            ? "checkout-box-mobile-v2"
            : ""
          : showCTA
          ? "checkout-box-desktop-v2"
          : ""
      }`}
    >
      {((!isPartnerLocation(selectedMembership?.locatable) && !location) ||
        !!location ||
        allSoldOut) &&
        showCTA &&
        showCheckoutPrice && (
          <div>
            {(!!quote || !!selectedMembership) &&
              showCTA &&
              showCheckoutPrice &&
              showPrice && (
                <Fragment>
                  <MembershipUnitPriceV2
                    price={
                      selectedMembership
                        ? selectedMembership.monthlyPrice
                        : quote?.subTotal || 0
                    }
                    priceUnit={fbt("/month")}
                    currency={
                      selectedMembership
                        ? selectedMembership.currency
                        : quote?.currency || ""
                    }
                    allSoldOut={allSoldOut}
                  />
                  <Spacer size={24} />
                </Fragment>
              )}
          </div>
        )}
      {!location && !hideLocationAndCityPicker && !quote && !allSoldOut && (
        <>
          <h3>{fbt("Select a location")}</h3>
          <Spacer size={24} />
        </>
      )}

      <CheckoutBoxDetails pickers={activePickers} />
      {error && (
        <Container paddingTop={16} paddingHorizontal={16}>
          {error}
        </Container>
      )}

      {mq.device === "mobile"
        ? showCTA && (
            <div className="col-4">
              <CheckoutCTAMembership
                soldOut={membershipSoldOut || allSoldOut}
                onContinue={onContinue}
                disabled={disabled || membershipSoldOut || allSoldOut}
              />
            </div>
          )
        : showCTA && (
            <CheckoutCTAMembership
              soldOut={membershipSoldOut || allSoldOut}
              onContinue={onContinue}
              disabled={disabled || membershipSoldOut || allSoldOut}
            />
          )}

      {(quote ||
        (selectedMembership && hideLocationAndCityPicker) ||
        allSoldOut) &&
        mq.device !== "mobile" && (
          <>
            <Spacer size={24} />
            <span>{fbt("Billed on or about the first of each month.")}</span>
          </>
        )}
    </div>
  );
}

interface UnitPriceProps {
  price: number;
  currency: string | null;
  priceUnit: string;
}

export function UnitPrice(props: UnitPriceProps) {
  const { price, currency, priceUnit } = props;
  const { device } = useMediaQuery();

  return (
    <div
      className={classNames(
        "fw-bold",
        device === "mobile" ? "h5" : "h3",
        "mb-0"
      )}
      data-testid="checkout-box-unit-price"
    >
      {formatCurrency(Math.ceil(price), currency, getSystemLocale())}
      {priceUnit}
    </div>
  );
}

interface MembershipUnitPriceProps {
  price: number;
  priceUnit: string;
  currency: string | null;
  allSoldOut?: boolean;
}

export function MembershipUnitPrice(props: MembershipUnitPriceProps) {
  const { price, currency, priceUnit } = props;

  const { fbt } = useI18n();

  return (
    <div>
      <h3>
        {formatCurrency(price, currency, getSystemLocale())}
        {priceUnit}
      </h3>
      <Spacer size={12} />

      <text>
        {fbt(
          "Billed on the 1st of the each month after your first prorated payment"
        )}
      </text>
    </div>
  );
}

export function MembershipUnitPriceV2(props: MembershipUnitPriceProps) {
  const { price, currency, priceUnit, allSoldOut } = props;
  const { fbt } = useI18n();
  const mq = useMediaQuery();

  return (
    <div>
      <h3 className="m-0">
        {formatCurrency(price, currency, getSystemLocale())}
        {priceUnit}
      </h3>
      {allSoldOut && (
        <span style={{ fontWeight: "bold" }} className="text-danger">
          {fbt("Sold out")}
        </span>
      )}

      {mq.device === "mobile" && (
        <>
          <Spacer size={12} />

          <div>{fbt("Billed on or about the first of each month.")}</div>
          <Spacer size={12} />
        </>
      )}
    </div>
  );
}

interface MembershipCheckoutPriceProps {
  todayPrice: number;
  currency: string | null;
  recurringPrice: number;
  recurringPriceWithPromoIteration: number;
  selectedMembership?: MembershipDetail;
  startDate?: string;
  promoLineItem: PromotionLine | null;
}

export function MembershipCheckoutPrice(props: MembershipCheckoutPriceProps) {
  const { todayPrice, currency, recurringPrice, selectedMembership } = props;
  const { fbt } = useI18n();

  const stepItems: Step[] = [
    {
      leftText: fbt("Today"),
      rightText: formatCurrency(todayPrice, currency, getSystemLocale()),
      isCompleted: true,
      helperText: fbt("Initial month prorated"),
    },
    {
      leftText: getFirstDayOfNextMonthAfterNMonths({
        addedMonths: selectedMembership?.firstPaymentCycles,
      }).toUpperCase(),
      rightText: formatCurrency(recurringPrice, currency, getSystemLocale()),
      isCompleted: false,
      helperText: fbt("Recurring monthly payment"),
    },
  ];

  return (
    <div>
      <h3>{fbt("Billing schedule")}</h3>
      <Spacer size={24} />
      <text>
        {fbt(
          "Billed on the 1st of the each month after your first prorated payment"
        )}
      </text>
      <Spacer size={24} />
      <Steps steps={stepItems} />
    </div>
  );
}

export function MembershipCheckoutPriceV2(props: MembershipCheckoutPriceProps) {
  const {
    todayPrice,
    currency,
    recurringPrice,
    recurringPriceWithPromoIteration,
    selectedMembership,
    startDate,
    promoLineItem,
  } = props;
  const { fbt } = useI18n();

  if (!selectedMembership) {
    return null;
  }

  const promoMonthsLeftAfterFirstPayment = getPromoMonthsLeftAfterFirstPayment({
    subscriptionIterations: promoLineItem?.subscriptionIterations,
    firstPaymentCycles: selectedMembership?.firstPaymentCycles,
  });

  const showSteps = shouldDisplayTimelineInMembershipCheckoutBox({
    firstPaymentCycles: selectedMembership?.firstPaymentCycles,
    promoMonthsLeftAfterFirstPayment,
  });

  const steps = getMembershipCheckoutBoxTimelineSteps({
    subscriptionIterations: promoLineItem?.subscriptionIterations,
    firstPaymentCycles: selectedMembership?.firstPaymentCycles,
    membershipStartDateString: startDate,
    currency,
    subscriptionPromotionEndsAt: promoLineItem?.subscriptionPromotionEndsAt,
    recurringPrice,
    todayPrice,
    recurringPriceWithPromoIteration,
    promoMonthsLeftAfterFirstPayment,
    fbt,
    timeZone: selectedMembership.timeZone,
  });

  return (
    <div>
      <div className="membership-total-price">
        <div className="first-bill-total">{fbt("First bill total")}</div>
        <div
          data-testid="membership-first-bill-total"
          className="first-bill-total"
        >
          {formatCurrency(todayPrice, currency, getSystemLocale())}
        </div>
      </div>

      {selectedMembership?.recurring && (
        <>
          <Spacer size={16} />
          {showSteps ? (
            <div
              data-testid="membership-steps"
              className="membership-steps-border"
            >
              <div>
                <strong>{fbt("Billing schedule")}</strong>
              </div>
              <Spacer size={8} />
              <span>{fbt("Billed on or about the first of each month.")}</span>
              <Spacer size={24} />
              <Steps steps={steps} />
            </div>
          ) : (
            <span
              data-testid="membership-reccurring-price-text"
              className="reccuring-payment-text"
            >
              {fbt(
                "After the initial month, you'll be billed " +
                  fbt.param(
                    "amount",
                    formatCurrency(recurringPrice, currency, getSystemLocale())
                  ) +
                  "/month on or about the first of the month."
              )}
            </span>
          )}
        </>
      )}
    </div>
  );
}

interface UnitPriceRangeProps {
  lowestPrice: number;
  highestPrice: number;
  currency: string | null;
  priceUnit: string;
}

function UnitPriceRange(props: UnitPriceRangeProps) {
  const { lowestPrice, highestPrice, currency, priceUnit } = props;

  const { fbt } = useI18n();
  const { device } = useMediaQuery();
  return (
    <div className={classNames("fw-bold", device === "mobile" ? "h5" : "h3")}>
      {lowestPrice !== highestPrice && `${fbt("From")} `}
      {formatCurrency(lowestPrice, currency, getSystemLocale())}
      {priceUnit}
    </div>
  );
}

interface CheckoutBoxMobileProps {
  checkoutBox: React.ReactNode;
  quote?: Quote | null | undefined;
  space: Space | null | undefined;
}

export function CheckoutBoxMobile(props: CheckoutBoxMobileProps) {
  const { checkoutBox, quote, space } = props;
  const [showCheckoutBox, setShowCheckoutBox] = useState<boolean>(false);
  const { fbt } = useI18n();
  const addOnOrderItem = quote?.orderItems.find(
    (o) => o.type === OrderableType.AddOn
  );
  const isWeWork = useIsWework();
  const { setOffset } = useContext(DiscountPopupContext);

  useEffect(() => {
    setOffset(100);

    return () => setOffset(0);
  }, [setOffset]);

  const closeCheckoutBoxModal = useCallback(() => {
    setShowCheckoutBox(false);
  }, [setShowCheckoutBox]);

  let checkoutPriceLabelComponent: JSX.Element | undefined;
  if (space) {
    if (quote) {
      if (
        addOnOrderItem?.type === OrderableType.AddOn &&
        addOnOrderItem.details.numberOfEntries > 0
      ) {
        checkoutPriceLabelComponent = (
          <UnitPrice
            price={quote.subTotal / addOnOrderItem.details.numberOfEntries}
            currency={space.currencyCode}
            priceUnit={fbt("/day")}
          />
        );
      } else if (quote.orderItems[0].type === OrderableType.Booking) {
        checkoutPriceLabelComponent = (
          <UnitPrice
            price={quote.orderItems[0].unitPrice}
            currency={space.currencyCode}
            priceUnit={fbt(
              quote.orderItems[0].details.type === BookingType.Hourly
                ? "/hour"
                : "/day"
            )}
          />
        );
      }
    } else {
      let hasDailyPrice = false;
      let hasHourlyPrice = false;
      space.pricings.forEach((p) => {
        if (p.type === PricingType.Daily) {
          hasDailyPrice = true;
        } else if (p.type === PricingType.Hourly) {
          hasHourlyPrice = true;
        }
      });

      if (
        hasDailyPrice &&
        hasHourlyPrice &&
        space.priceRanges.hourly &&
        space.priceRanges.daily
      ) {
        const maxHours = getMaxHoursFromBookingHours(space.bookingHours);

        const savingPercent = calculateDailyBenefitPercent(
          space.pricings.find((p) => p.type === PricingType.Hourly)?.price,
          space.priceRanges.daily.lowestPrice,
          maxHours?.openAt,
          maxHours?.closedAt
        );
        checkoutPriceLabelComponent = (
          <PriceLabel
            priceRanges={space.priceRanges}
            currency={space.currencyCode}
            hourlyPrice={space.priceRanges.hourly.lowestPrice}
            dailySavingPercent={savingPercent}
          />
        );
      } else if (hasHourlyPrice && space.priceRanges.hourly) {
        checkoutPriceLabelComponent = (
          <UnitPriceRange
            lowestPrice={space.priceRanges.hourly.lowestPrice}
            highestPrice={space.priceRanges.hourly.highestPrice}
            currency={space.currencyCode}
            priceUnit={isWeWork ? fbt("before VAT per hour") : fbt("/hour")}
          />
        );
      } else if (hasDailyPrice && space.priceRanges.daily) {
        checkoutPriceLabelComponent = (
          <UnitPriceRange
            lowestPrice={space.priceRanges.daily.lowestPrice}
            highestPrice={space.priceRanges.daily.highestPrice}
            currency={space.currencyCode}
            priceUnit={isWeWork ? ` ${fbt("before VAT per day")}` : fbt("/day")}
          />
        );
      }
    }
  }

  return (
    <div className="checkout-box-mobile-dialog position-fixed bottom-0 start-0 end-0">
      <ListGroup>
        <ListGroup.Item
          className="py-4 px-3"
          style={{
            display: showCheckoutBox ? "none" : "flex",
            justifyContent: "space-between",
            borderRadius: 0,
            alignItems: "center",
          }}
        >
          <div>{checkoutPriceLabelComponent}</div>
          <Button onClick={() => setShowCheckoutBox(true)}>
            {fbt("Book this space")}
          </Button>
        </ListGroup.Item>
      </ListGroup>
      <Modal
        show={showCheckoutBox}
        onHide={closeCheckoutBoxModal}
        fullscreen="sm-down"
        aria-labelledby="contained-modal-title-vcenter"
        className="header-center checkout-box-mobile-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title as="h6">{fbt("Select booking details")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{checkoutBox}</Modal.Body>
      </Modal>
    </div>
  );
}

interface MembershipCheckoutBoxMobileProps {
  checkoutBox: React.ReactNode;
  quote?: Quote | null | undefined;
  membershipDetail?: MembershipDetail;
  showPrice: boolean;
  hideAllPickers?: boolean;
  onContinue?: () => void;
  allSoldOut?: boolean;
}

export function MembershipCheckoutBoxMobile(
  props: MembershipCheckoutBoxMobileProps
) {
  const {
    checkoutBox,
    showPrice,
    membershipDetail,
    hideAllPickers,
    onContinue,
    allSoldOut,
  } = props;
  const [showCheckoutBox, setShowCheckoutBox] = useState<boolean>(false);
  const { fbt } = useI18n();

  const closeCheckoutBoxModal = useCallback(() => {
    setShowCheckoutBox(false);
  }, [setShowCheckoutBox]);

  const onClick = () => {
    if (!hideAllPickers) {
      setShowCheckoutBox(true);
    } else if (onContinue) {
      onContinue();
    }
  };

  return (
    <div className="checkout-box-mobile-dialog position-fixed bottom-0 start-0 end-0">
      <ListGroup>
        <ListGroup.Item
          className="py-4 px-3"
          style={{
            display: showCheckoutBox ? "none" : "flex",
            justifyContent: "space-between",
            borderRadius: 0,
            alignItems: "center",
          }}
        >
          <h5 className="fw-bold pt-1">
            {showPrice && membershipDetail && (
              <>
                <div>
                  <UnitPrice
                    price={membershipDetail.monthlyPrice}
                    currency={membershipDetail.currency}
                    priceUnit={fbt("/month")}
                  />
                </div>
                {allSoldOut && (
                  <span style={{ fontSize: "16px" }} className="text-danger">
                    {fbt("Sold out")}
                  </span>
                )}
              </>
            )}
          </h5>
          <Button disabled={allSoldOut} onClick={onClick}>
            {allSoldOut
              ? fbt("Sold out")
              : hideAllPickers
              ? fbt("Continue")
              : fbt("Buy now")}
          </Button>
        </ListGroup.Item>
      </ListGroup>
      <Modal
        show={showCheckoutBox}
        onHide={closeCheckoutBoxModal}
        fullscreen="sm-down"
        aria-labelledby="contained-modal-title-vcenter"
        className="header-center checkout-box-mobile-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title as="h5">{fbt("Select details")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{checkoutBox}</Modal.Body>
      </Modal>
    </div>
  );
}

interface CheckoutBoxDetailsProps {
  pickers: React.ReactNode[];
}

export function CheckoutBoxDetails(props: CheckoutBoxDetailsProps) {
  const { pickers } = props;

  return (
    <div className="checkout-box-details">
      <ListGroup>
        {pickers.map((picker, i) => {
          return <ListGroup.Item>{picker}</ListGroup.Item>;
        })}
      </ListGroup>
    </div>
  );
}

interface CheckoutCTAProps {
  disabled: boolean;
  onContinue?: () => void;
}

function CheckoutCTA(props: CheckoutCTAProps) {
  const { disabled, onContinue } = props;
  const { fbt } = useI18n();
  const analytics = useAnalytics();
  const mrGalleryImprovementsFeatureFlag =
    useMrGalleryImprovementsFeatureFlag();

  return (
    <div className="checkout-box-continue">
      <Spacer size={24} />
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => {
          if (onContinue) onContinue();

          analytics.event(
            "Quote Box Continue Click",
            {
              Source: "WL",
            },
            { onlyProviders: ["mixpanel", "customerio"] }
          );
        }}
        disabled={disabled}
      >
        {mrGalleryImprovementsFeatureFlag
          ? fbt("Continue to checkout")
          : fbt("Continue")}
      </button>
    </div>
  );
}

interface CheckoutCTAMembershipProps {
  disabled: boolean;
  onContinue?: () => void;
  soldOut: boolean;
}
function CheckoutCTAMembership(props: CheckoutCTAMembershipProps) {
  const { disabled, onContinue, soldOut } = props;
  const { fbt } = useI18n();
  const analytics = useAnalytics();

  return (
    <div className="checkout-box-continue">
      <Spacer size={24} />
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => {
          if (onContinue) onContinue();

          analytics.event(
            "Quote Box Continue Click",
            {
              Source: "WL",
            },
            { onlyProviders: ["mixpanel", "customerio"] }
          );
        }}
        disabled={disabled}
      >
        {soldOut ? fbt("Sold out") : fbt("Continue")}
      </button>
    </div>
  );
}

interface MembershipCheckoutBoxDesktopV2Props {
  quote: Quote | undefined;
  quoteError?: string;
  quoteLoading: boolean;
  locations: Location[];
  location: Location | null | undefined;
  orderParams: MembershipOrderParams;
  orderParamsErrors?: MembershipOrderParamsErrors;
  onChangeLocation: (spaceID: string) => void;
  onChangeCity: (city: string) => void;
  onContinue?: () => void;

  // UI overrides
  showCTA?: boolean;
  datePickerPlaceholder?: string;
  showCheckoutPrice?: boolean;
  showDiscountLabel?: boolean;
  showPriceInfoMessage?: boolean;
  selectedMembership?: MembershipDetail;
  offeringLocations?: PartnerOfferingLocation[];
  onChangeStartDate?: (startDate: string) => void;
  showStartDate?: boolean;
  soldOut: boolean;
}

export function MembershipCheckoutBoxDesktop(
  props: MembershipCheckoutBoxDesktopV2Props
) {
  const {
    quote,
    orderParams,
    locations,
    location,
    onChangeLocation,
    onChangeCity,
    onContinue,
    showCTA,
    showCheckoutPrice,
    showPriceInfoMessage,
    selectedMembership,
    offeringLocations,
    soldOut,
  } = props;
  let error;

  /**
   * @todo
   * @Emre will delete this
   */
  const [capacity, setCapacity] = useState(1);
  const params: any = useParams();
  const offeringID = params.offeringID;

  const handleChangeCity = useCallback(
    (city) => {
      onChangeCity(city);
      const cityLocations = locations.filter(
        (location) => location.city === city
      );
      if (cityLocations.length === 1) {
        onChangeLocation(cityLocations[0].id);
      }
    },
    [locations, onChangeCity, onChangeLocation]
  );

  const filteredLocations = locations.filter((l) =>
    orderParams.city ? l.city === orderParams.city : l
  );

  const cities = useMemo(
    () =>
      uniqueBy(
        locations.map((l) => ({
          value: l.city as string,
          label: l.city as string,
          data: l,
        })),
        (l) => l.value
      ),
    [locations]
  );

  const hideLocationAndCityPicker =
    shouldHideLocationAndCityPicker(offeringLocations);

  return (
    <CheckoutBoxForMembershipDesktop
      location={location}
      quote={quote}
      error={error}
      onContinue={onContinue}
      showCTA={showCTA}
      priceType={PricingType.Daily}
      showCheckoutPrice={showCheckoutPrice}
      showPriceInfoMessage={showPriceInfoMessage}
      showPrice={!!location || hideLocationAndCityPicker}
      selectedMembership={selectedMembership}
      hideLocationAndCityPicker={hideLocationAndCityPicker}
      membershipSoldOut={soldOut}
      allSoldOut={soldOut}
      pickers={
        hideLocationAndCityPicker
          ? []
          : [
              cities.length > 1 && (
                <CityPicker
                  cities={cities}
                  value={orderParams.city}
                  onChange={handleChangeCity}
                />
              ),
              <MembershipLocationPickerWrapper
                value={orderParams.locationID}
                locations={filteredLocations}
                onChange={onChangeLocation}
                disabled={cities.length > 1 ? !orderParams.city : false}
              />,
              /**
               * @todo
               * @Emre will update here when capacity api is ready
               */
              offeringID === "mindspace-private-office-membership" && (
                <CapacityPickerV2
                  capacities={[4, 5, 6, 7]}
                  value={capacity}
                  onChange={setCapacity}
                  disabled={false}
                />
              ),
            ]
      }
    />
  );
}

export function MembershipCheckoutBoxDesktopV2(
  props: MembershipCheckoutBoxDesktopV2Props
) {
  const {
    quote,
    orderParams,
    locations,
    location,
    onChangeLocation,
    onChangeCity,
    onContinue,
    showCTA,
    showCheckoutPrice,
    showPriceInfoMessage,
    selectedMembership,
    onChangeStartDate,
    showStartDate,
    offeringLocations,
    quoteError,
    soldOut,
  } = props;
  let error;

  /**
   * @todo
   * @Emre will delete this
   */
  const [capacity, setCapacity] = useState(1);
  const params: any = useParams();
  const offeringID = params.offeringID;
  const queryParams = useQueryString();

  const hasNoOfferingErrorFromQueryLocationId = useMemo(
    () =>
      hasFoundOfferingFromQueryLocationId(
        offeringLocations,
        queryParams.locationID
      ) ||
      !queryParams.locationID ||
      !offeringLocations,
    [offeringLocations, queryParams.locationID]
  );
  const hideLocationAndCityPicker =
    shouldHideLocationAndCityPicker(offeringLocations) &&
    hasNoOfferingErrorFromQueryLocationId;

  const { data: availableDays, loading: availableDaysLoading } =
    useGetAvailableDaysMembership(
      !hideLocationAndCityPicker && location
        ? selectedMembership?.id
        : undefined
    );

  const handleChangeCity = useCallback(
    (city) => {
      onChangeCity(city);
      const cityLocations = locations.filter(
        (location) => location.city === city
      );
      if (cityLocations.length === 1) {
        onChangeLocation(cityLocations[0].id);
      }
    },
    [locations, onChangeCity, onChangeLocation]
  );

  const filteredLocations = locations.filter((l) =>
    orderParams.city ? l.city === orderParams.city : l
  );

  const cities = useMemo(
    () =>
      uniqueBy(
        locations.map((l) => ({
          value: l.city.trim() as string,
          label: l.city.trim() as string,
          data: l,
          soldOut: findIsSoldOut(
            locations.filter((loc) => loc.city === l.city),
            offeringLocations
          ),
        })),
        (l) => l.value
      ),
    [locations, offeringLocations]
  );

  const isAllSoldOut = findIsSoldOut(
    hideLocationAndCityPicker ? [] : locations,
    offeringLocations
  );

  const firstAvailableDay =
    availableDays && availableDays.length
      ? toDay(new Date(availableDays[0]))
      : toDay(new Date());

  useEffect(() => {
    if (
      availableDays &&
      availableDays.length &&
      onChangeStartDate &&
      (!orderParams.subscriptionStartDate ||
        !availableDays.includes(orderParams.subscriptionStartDate))
    ) {
      onChangeStartDate(availableDays[0]);
    }
  }, [availableDays, onChangeStartDate, orderParams.subscriptionStartDate]);

  return (
    <CheckoutBoxForMembershipDesktopV2
      location={location}
      quote={quote}
      error={error}
      onContinue={onContinue}
      showCTA={showCTA}
      priceType={PricingType.Daily}
      showCheckoutPrice={showCheckoutPrice}
      showPriceInfoMessage={showPriceInfoMessage}
      showPrice={!!location || hideLocationAndCityPicker || isAllSoldOut}
      selectedMembership={selectedMembership}
      hideLocationAndCityPicker={hideLocationAndCityPicker}
      quoteError={quoteError}
      membershipSoldOut={soldOut}
      allSoldOut={isAllSoldOut}
      pickers={
        isAllSoldOut && !hideLocationAndCityPicker
          ? []
          : [
              !hideLocationAndCityPicker && (
                <MembershipCityPicker
                  cities={cities}
                  value={orderParams.city}
                  onChange={handleChangeCity}
                />
              ),
              !hideLocationAndCityPicker && (
                <MembershipLocationPickerWrapper
                  value={orderParams.locationID}
                  locations={filteredLocations}
                  onChange={onChangeLocation}
                  disabled={cities.length > 1 ? !orderParams.city : false}
                  offeringLocations={offeringLocations}
                />
              ),
              /**
               * @todo
               * @Emre will update here when capacity api is ready
               */
              offeringID === "mindspace-private-office-membership" &&
                !hideLocationAndCityPicker && (
                  <CapacityPickerV2
                    capacities={[4, 5, 6, 7]}
                    value={capacity}
                    onChange={setCapacity}
                    disabled={false}
                  />
                ),
              showStartDate && !isAllSoldOut && !!onChangeStartDate && (
                <MembershipDatePickerWrapper
                  onComplete={(day) => onChangeStartDate(day)}
                  value={
                    orderParams.subscriptionStartDate
                      ? parseDay(orderParams.subscriptionStartDate)
                      : undefined
                  }
                  availableDates={availableDays}
                  isBlocked={(date) =>
                    availableDaysLoading ||
                    isBefore(toDay(date), firstAvailableDay) ||
                    isAfter(toDay(date), addMonths(firstAvailableDay, 1)) ||
                    (!!availableDays && !availableDays.includes(toDay(date)))
                  }
                />
              ),
            ]
      }
    />
  );
}
